// Where a form-bound row's `name` and `description` come from.
//
// A CmForm is just a set of questions — we don't make the author nominate a "name
// field" or a "description field". Both columns are therefore DERIVED config, and
// the two are symmetric (see
// docs/developer-guides/llm-context/2026-09-23_cmform-name-description-derivation.md):
//
//   name        ← `schema.title_fields`       (ordered field keys, space-joined)
//   description ← `schema.description_fields` (ordered field keys, space-joined)
//
// Each also honours a legacy explicit binding — a field bound to `target:"name"` /
// `target:"description"` — which the builder no longer offers but older forms still
// carry, so it wins where present. Every field authored from now on keeps its own
// `row_data` column and is merely *referenced* by these lists.
//
// The concatenation must match what the backend/mobile produce for the same row, so
// the title previewed while capturing is the title that sticks.

import { FormSchema, SchemaField } from "../../types/fieldForm";
import { CaptureValues, captureKeyOf } from "./captureKeys";

/**
 * How one derived row column is produced. Identical for the name and the
 * description — only the schema list they read from differs.
 */
export type DerivedSource =
  /** A legacy field bound straight to the column — it IS the value. */
  | { kind: "field"; field: SchemaField }
  /** The ordered picked fields are concatenated into the column. */
  | { kind: "fields"; fields: SchemaField[] }
  /** The form says nothing about this column. */
  | { kind: "manual" };

/** Values are joined with a single space — the same rule the backend must apply. */
const JOIN_SEPARATOR = " ";

/** Max picked fields per derived column, mirroring Fulcrum's five title slots. */
export const MAX_DERIVED_FIELDS = 5;

const isFillable = (f: SchemaField) => f.kind !== "display";

/**
 * Resolve how one derived column is produced. A legacy explicit binding wins over
 * the picked list (the builder enforces the same precedence), and picked keys that
 * no longer resolve to a fillable field are ignored — a stale key must not leave
 * the column deriving from nothing.
 */
function resolveSource(schema: FormSchema | null | undefined, column: "name" | "description", picked: string[]): DerivedSource {
  const fields = schema?.fields ?? [];
  const explicit = fields.find((f) => isFillable(f) && f.target === column);
  if (explicit) return { kind: "field", field: explicit };

  const resolved = picked.map((key) => fields.find((f) => f.key === key)).filter((f): f is SchemaField => !!f && isFillable(f));
  if (resolved.length) return { kind: "fields", fields: resolved };

  return { kind: "manual" };
}

/** How this schema names its rows (`title_fields`, or a legacy `target:"name"`). */
export function resolveNameSource(schema: FormSchema | null | undefined): DerivedSource {
  return resolveSource(schema, "name", schema?.title_fields ?? []);
}

/** How this schema describes its rows (`description_fields`, or a legacy target). */
export function resolveDescriptionSource(schema: FormSchema | null | undefined): DerivedSource {
  return resolveSource(schema, "description", schema?.description_fields ?? []);
}

/**
 * A single field value as it reads in a title: choice labels rather than raw
 * codes, and a yes/no rendered with the author's own labels.
 */
export function titleValueOf(field: SchemaField, value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((v) => titleValueOf(field, v))
      .filter((s) => s !== "")
      .join(", ");
  }
  if (field.widget === "yesno") {
    const raw = String(value).trim().toLowerCase();
    if (raw === "") return "";
    const positive = raw === "true" || raw === "yes" || raw === "1";
    const negative = raw === "false" || raw === "no" || raw === "0";
    if (positive) return field.positive_label || "Yes";
    if (negative) return field.negative_label || "No";
    // N/A (or any other stored value) reads as itself.
    return String(value).trim();
  }
  return String(value).trim();
}

/** Concatenate a source's non-empty values, space-separated, in author order. */
function buildFromSource(source: DerivedSource, values: CaptureValues): string {
  if (source.kind === "manual") return "";
  const fields = source.kind === "field" ? [source.field] : source.fields;
  return fields
    .map((f) => {
      const key = captureKeyOf(f.target);
      return key ? titleValueOf(f, values[key]) : "";
    })
    .filter((s) => s !== "")
    .join(JOIN_SEPARATOR);
}

/**
 * Build the row title from the captured values. Returns "" when nothing has been
 * captured yet (the caller then leaves the row unnamed and lets the backend apply
 * its `<form name> + <uuid>` fallback).
 */
export function buildRecordTitle(schema: FormSchema | null | undefined, values: CaptureValues): string {
  return buildFromSource(resolveNameSource(schema), values);
}

/**
 * Build the row description from the captured values. Returns "" when the form
 * derives no description — the column stays blank, which is allowed.
 */
export function buildRecordDescription(schema: FormSchema | null | undefined, values: CaptureValues): string {
  return buildFromSource(resolveDescriptionSource(schema), values);
}

/** "Activity Name, Is Funded Trip?" — the labels a derived column is built from. */
export function describeSource(source: DerivedSource): string {
  if (source.kind === "manual") return "";
  const fields = source.kind === "field" ? [source.field] : source.fields;
  return fields.map((f) => f.label || f.key).join(", ");
}

/** The labels the title is built from. */
export function describeTitleSource(schema: FormSchema | null | undefined): string {
  return describeSource(resolveNameSource(schema));
}

/** The labels the description is built from. */
export function describeDescriptionSource(schema: FormSchema | null | undefined): string {
  return describeSource(resolveDescriptionSource(schema));
}

/**
 * Whether the host should adopt a freshly derived value (title or description).
 *
 * The rule exists because a row's stored value and the one its picked fields
 * produce can legitimately differ — the row may predate the current picks, or the
 * backend may format the concatenation slightly differently. Opening such a record
 * must not silently rewrite it; only a real edit to a picked field does.
 *
 * - `baseline === null` → first pass for this record/schema: adopt only when
 *   there is nothing to preserve (a new record, or a row with no name).
 * - otherwise → adopt exactly when the derived title actually changed.
 */
export function shouldAdoptDerivedTitle(params: {
  /** The derived value as this record was loaded, or null on the first pass. */
  baseline: string | null;
  /** The value derived from what is captured right now. */
  derived: string;
  /** The value currently held by the host. */
  currentName: string;
  mode: "create" | "edit";
}): boolean {
  const { baseline, derived, currentName, mode } = params;
  if (baseline === null) return mode === "create" || !currentName.trim();
  return derived !== baseline;
}
